/**
 * «Operativ xarita» — the fleet on the city's own geography.
 *
 * ============================================================================
 * REAL COORDINATES, OR NO DOT AT ALL.
 * ============================================================================
 *
 * The positions come from `mahallas.center` in the registry — the hokimiyat's
 * own coordinates, joined to live fleet status. A mahalla with no coordinate is
 * NOT placed somewhere plausible; it is listed under the map as unplaced, with
 * its status intact.
 *
 * That refusal is the whole point of the screen. A decorative map that arranges
 * kiosks in a pleasing constellation is worse than a table, because it invites
 * exactly the decision a map is for — "the failures are all in the north, send
 * the van there" — and answers it with a layout somebody chose for looks. If
 * this screen shows a dot, that dot is where the building is.
 *
 * The projection is equirectangular over the bounding box of the placed points.
 * At city scale (Navoiy spans ~0.1°) the distortion is far below one pixel, and
 * the alternative — a tile layer — would mean an external map host on a screen
 * that must work when the province's link to the outside is what failed.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { components } from '@navoiy/api-types';
import { api, ApiError, unwrap } from '../api/client.js';
import { useSession } from '../auth/SessionContext.js';
import { useFleet, type MbtKiosk } from '../realtime/useFleet.js';
import { t } from '../i18n/index.js';
import { ageLabel } from '../lib/format.js';
import { EmptyState, Notice, Panel, PanelHeader, Skeleton, StatusPill } from '../ui/index.jsx';
import { PageHeader } from '../ui/console.jsx';
import { LiveBadge } from '../ui/data.jsx';
import { Meter } from '../ui/charts.jsx';

type MahallaSummary = components['schemas']['MahallaSummary'];

interface PlacedKiosk {
  readonly kiosk: MbtKiosk;
  readonly x: number;
  readonly y: number;
}

/** The status dot's colour — reserved status tokens, always with a label. */
function toneOf(status: string): string {
  return status === 'critical'
    ? 'text-crit'
    : status === 'offline'
      ? 'text-offline'
      : status === 'warning'
        ? 'text-warn'
        : status === 'maintenance'
          ? 'text-brand'
          : 'text-ok';
}

export function OperativMapScreen(): React.JSX.Element {
  const { expire } = useSession();
  const onExpired = useCallback(() => expire(), [expire]);
  const fleet = useFleet(onExpired);

  const [mahallas, setMahallas] = useState<MahallaSummary[] | null>(null);
  const [selected, setSelected] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = unwrap(await api().GET('/mahallas', {}));
        if (!cancelled) setMahallas(data.items as MahallaSummary[]);
      } catch (caught) {
        if (caught instanceof ApiError && caught.status === 401) onExpired();
        else if (!cancelled) setMahallas([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [onExpired]);

  /**
   * Project the kiosks whose mahalla has a real coordinate.
   *
   * The padding keeps a dot at the extreme edge from being clipped by its own
   * glow, which at the corners is the difference between "offline kiosk" and
   * "half a red smudge".
   */
  const { placed, unplaced, bounds } = useMemo(() => {
    const byMahalla = new Map<string, MahallaSummary>();
    for (const mahalla of mahallas ?? []) byMahalla.set(mahalla.id, mahalla);

    const withCoords: { kiosk: MbtKiosk; lat: number; lon: number }[] = [];
    const without: MbtKiosk[] = [];
    for (const kiosk of fleet.kiosks) {
      const centre = byMahalla.get(kiosk.mahalla_id)?.center;
      if (centre && typeof centre.lat === 'number' && typeof centre.lon === 'number') {
        withCoords.push({ kiosk, lat: centre.lat, lon: centre.lon });
      } else {
        without.push(kiosk);
      }
    }
    if (withCoords.length === 0) {
      return { placed: [] as PlacedKiosk[], unplaced: without, bounds: null };
    }

    const lats = withCoords.map((entry) => entry.lat);
    const lons = withCoords.map((entry) => entry.lon);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLon = Math.min(...lons);
    const maxLon = Math.max(...lons);
    // A single point, or a perfectly straight row of them, would divide by zero.
    const latSpan = maxLat - minLat || 0.01;
    const lonSpan = maxLon - minLon || 0.01;
    const pad = 8;

    return {
      placed: withCoords.map((entry) => ({
        kiosk: entry.kiosk,
        x: pad + ((entry.lon - minLon) / lonSpan) * (100 - pad * 2),
        // SVG y grows downward; latitude grows north. The flip is what stops
        // the map being upside down, which on a city an operator knows by heart
        // is the kind of error that destroys trust in the whole screen.
        y: pad + (1 - (entry.lat - minLat) / latSpan) * (100 - pad * 2),
      })),
      unplaced: without,
      bounds: { minLat, maxLat, minLon, maxLon },
    };
  }, [fleet.kiosks, mahallas]);

  const chosen = placed.find((entry) => entry.kiosk.id === selected)?.kiosk ?? null;
  const counts = useMemo(() => {
    const map = new Map<string, number>();
    for (const kiosk of fleet.kiosks) map.set(kiosk.status, (map.get(kiosk.status) ?? 0) + 1);
    return map;
  }, [fleet.kiosks]);

  if (fleet.error === 'forbidden') {
    return <Notice tone="crit">{t('error.forbidden')}</Notice>;
  }

  return (
    <div className="space-y-4">
      <PageHeader
        eyebrow={
          <>
            {fleet.live ? <LiveBadge label={t('status.live')} /> : null}
            <span>{t('map.eyebrow')}</span>
          </>
        }
        title={t('nav.map')}
        subtitle={t('map.subtitle', { placed: placed.length, total: fleet.kiosks.length })}
        right={
          /* The freshness line belongs WITH the figure, not on a bare row under
             the header — it qualifies the number («0 placed, as of a minute
             ago»), and a stray timestamp floating on the canvas reads as a
             leftover from the previous layout, which is exactly what it was. */
          <div className="text-right">
            <p className="text-hero font-bold tabular-nums leading-none text-ink-100">
              {placed.length}
            </p>
            {fleet.fetchedAt === null ? null : (
              <p className="mt-1 text-micro text-ink-500">
                {t('app.asOf', { at: ageLabel(fleet.fetchedAt, fleet.now, t) })}
              </p>
            )}
          </div>
        }
      />

      {fleet.error === 'network' ? <Notice tone="warn">{t('error.network')}</Notice> : null}
      {fleet.scoped ? <Notice tone="quiet">{t('fleet.scopedNote')}</Notice> : null}

      <div className="grid gap-4 xl:grid-cols-[2fr_1fr]">
        <Panel>
          <PanelHeader
            title={t('nav.map')}
            aside={
              <div className="flex flex-wrap items-center gap-3 text-micro">
                {/* The legend, always present: identity is never colour alone,
                    and on a map that rule matters more than anywhere else. */}
                {['healthy', 'warning', 'critical', 'offline', 'maintenance'].map((status) => (
                  <span key={status} className="flex items-center gap-1.5">
                    <span
                      className={`h-2 w-2 rounded-full ${toneOf(status)}`}
                      style={{ backgroundColor: 'currentColor' }}
                    />
                    <span className="text-ink-500">{t(`status.${status}`)}</span>
                    <span className="tabular-nums text-ink-300">{counts.get(status) ?? 0}</span>
                  </span>
                ))}
              </div>
            }
          />
          <div className="px-4 py-4">
            {fleet.loading || mahallas === null ? (
              <Skeleton className="h-[32rem]" />
            ) : placed.length === 0 ? (
              <EmptyState message={t('map.noCoordinates')} />
            ) : (
              <svg
                viewBox="0 0 100 100"
                className="h-[32rem] w-full rounded bg-canvas"
                role="img"
                aria-label={t('map.alt')}
              >
                {/* A recessive grid — orientation, not decoration. It is the
                    only thing that makes the dots read as a place rather than a
                    scatter plot. */}
                <defs>
                  <pattern id="mapgrid" width="10" height="10" patternUnits="userSpaceOnUse">
                    <path
                      d="M 10 0 L 0 0 0 10"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="0.15"
                      className="text-hairline"
                    />
                  </pattern>
                </defs>
                <rect width="100" height="100" fill="url(#mapgrid)" />

                {placed.map((entry) => {
                  const tone = toneOf(entry.kiosk.status);
                  const isChosen = entry.kiosk.id === selected;
                  return (
                    <g
                      key={entry.kiosk.id}
                      className={`${tone} cursor-pointer`}
                      onClick={() => setSelected(isChosen ? null : entry.kiosk.id)}
                    >
                      {/* The glow. Only on the two statuses that mean "act" —
                          a map where every dot glows is a map with no signal. */}
                      {entry.kiosk.status === 'critical' || entry.kiosk.status === 'offline' ? (
                        <circle cx={entry.x} cy={entry.y} r="4" fill="currentColor" opacity="0.18" />
                      ) : null}
                      <circle
                        cx={entry.x}
                        cy={entry.y}
                        r={isChosen ? 2.4 : 1.6}
                        fill="currentColor"
                        stroke="currentColor"
                        strokeWidth="0.4"
                      />
                      {/* Hit target far bigger than the mark — 1.6 SVG units is
                          a few pixels, and nobody clicks that on a touchscreen. */}
                      <circle cx={entry.x} cy={entry.y} r="5" fill="transparent" />
                      <title>{`${entry.kiosk.short_code} · ${entry.kiosk.mahalla_name}`}</title>
                    </g>
                  );
                })}
              </svg>
            )}
            {bounds === null ? null : (
              <p className="mt-2 text-micro tabular-nums text-ink-500">
                {t('map.bounds', {
                  north: bounds.maxLat.toFixed(3),
                  south: bounds.minLat.toFixed(3),
                  east: bounds.maxLon.toFixed(3),
                  west: bounds.minLon.toFixed(3),
                })}
              </p>
            )}
          </div>
        </Panel>

        <div className="space-y-4">
          <Panel>
            <PanelHeader title={t('map.selected')} />
            <div className="px-4 py-4">
              {chosen === null ? (
                <p className="text-label text-ink-500">{t('map.selectHint')}</p>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="truncate text-lead font-semibold text-ink-100">{chosen.label}</p>
                      <p className="truncate text-micro text-ink-500">
                        {chosen.short_code} · {chosen.mahalla_name}
                      </p>
                    </div>
                    <StatusPill status={chosen.status} label={t(`status.${chosen.status}`)} />
                  </div>
                  <Meter
                    label={t('fleet.metric.cpu')}
                    pct={chosen.telemetry?.health?.cpu_pct ?? null}
                    display={`${Math.round(chosen.telemetry?.health?.cpu_pct ?? 0)}%`}
                    tone={(chosen.telemetry?.health?.cpu_pct ?? 0) > 85 ? 'warn' : 'neutral'}
                  />
                  <Meter
                    label={t('fleet.metric.disk')}
                    pct={100 - (chosen.telemetry?.health?.disk_free_pct ?? 100)}
                    display={`${Math.round(chosen.telemetry?.health?.disk_free_pct ?? 0)}%`}
                    tone={(chosen.telemetry?.health?.disk_free_pct ?? 100) < 10 ? 'crit' : 'neutral'}
                  />
                  <p className="text-micro tabular-nums text-ink-500">
                    {t('map.queue', { n: String(chosen.telemetry?.work?.queue_depth ?? 0) })}
                  </p>
                </div>
              )}
            </div>
          </Panel>

          {/*
            The kiosks the map REFUSED to place. Listed rather than hidden: a
            mahalla with no registry coordinate is a data gap somebody can fix,
            and a map that quietly dropped three kiosks would be a map an
            operator counts as complete.
          */}
          {unplaced.length > 0 ? (
            <Panel>
              <PanelHeader title={t('map.unplaced')} aside={String(unplaced.length)} />
              <div className="space-y-2 px-4 py-4">
                <p className="text-micro text-ink-500">{t('map.unplacedHint')}</p>
                {unplaced.map((kiosk) => (
                  <div key={kiosk.id} className="flex items-center justify-between gap-2">
                    <span className="truncate text-micro text-ink-300">
                      {kiosk.short_code} · {kiosk.mahalla_name}
                    </span>
                    <StatusPill status={kiosk.status} label={t(`status.${kiosk.status}`)} />
                  </div>
                ))}
              </div>
            </Panel>
          ) : null}
        </div>
      </div>
    </div>
  );
}
